import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker, { registerLocale } from "react-datepicker";

import ru from "date-fns/locale/ru";
import moment from "moment";
import clsx from "clsx";

import {
  actionGetTemplateUnloadingsFilters,
  actionGetTemplateUnloadings,
} from "../../../redux/action/unloadings/actionTemplateUnloadings";
import {
  formatUnloadingsDate,
  formatUnloadingsTime,
} from "../../../utils/formatDate";
import {
  reducerTemplateUnloadingsInputValues,
  reducerTemplateOpenErrorsModal,
} from "../../../redux/reducer/unloadings/reducers/templateUnloadings";
import {
  createOptions,
  createSelectOptions,
} from "../../../utils/createOptions";
import { AppDispatch } from "../../../redux/reducer/store";

import ErrorsModal from "../../errorsModal/ErrorsModal";

import LimitSelect from "../../custom/limitSelect/limitSelect";
import Pagination from "../../pagination/pagination";
import Loader from "../../custom/loader/loader";
import Select from "../../custom/select/select";

import RefreshIcon from "../../../assets/icons/refresh.svg";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

interface ITemplateUnloadings {
  date: string;
  employee: string;
  error: string;
  order: { id: number };
  status: { [key: string]: string };
  time: string;
  type: string;
  url?: string;
}

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const timeOptions = [
  { value: "", label: "Все" },
  { value: "1", label: "Больше минуты" },
  { value: "10", label: "Больше 10 минут" },
];

const typeOptions = [
  { value: "", label: "Все" },
  { value: "Шаблон отчетов EasyMerch", label: "Шаблон на создание отчетов" },
  { value: "Загрузка шаблона KPI", label: "Шаблон KPI" },
  { value: "Шаблон Смена начало смены", label: "Шаблон Смена начало смены" },
  { value: "Загрузка коэффициентов бюджета для контрактов", label: "Коэффициенты бюджета для контрактов" },
];

const TableAdminUnloadingTemplate: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    templateUnloadingsInputValues,
    templateUnloadingsCount,
    templateUnloadings,
    openErrorsModal,
    filters,
    limit,
    page,
  } = useSelector((state: any) => state.unloadings.templateUnloadings);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );

  const statusesOptions = createSelectOptions(filters?.statuses);

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    templateUnloadingsInputValues.date &&
      new Date(templateUnloadingsInputValues.date)
  );
  const [selectedStatusOption, setSelectedStatusOption] = useState<string>("");
  const [isInputChange, setIsInputChange] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetTemplateUnloadings(actualLimit, page)).then(() => {
      setIsPending(false);
    });
    dispatch(actionGetTemplateUnloadingsFilters());
    registerLocale("ru", ru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const value = event.value;
    if (value === "") {
      if (name === "status") {
        setSelectedStatusOption("all");
      }

      const updatedInputValues = { ...templateUnloadingsInputValues };
      delete updatedInputValues[name];

      dispatch(reducerTemplateUnloadingsInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerTemplateUnloadingsInputValues({
          ...templateUnloadingsInputValues,
          [name]:
            name === "moreThanMin"
              ? parseFloat(value)
              : name === "employee" || name === "customer"
              ? { id: value }
              : value,
        })
      );
    }

    setIsPending(true);
    dispatch(actionGetTemplateUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
    resetPages();
  };

  const handleDatePickerChange = (date: Date) => {
    setSelectedDate(date);

    const formattedDate = moment(date).format("YYYY-MM-DD");

    dispatch(
      reducerTemplateUnloadingsInputValues({
        ...templateUnloadingsInputValues,
        date: formattedDate,
      })
    );

    setIsPending(true);
    dispatch(actionGetTemplateUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleClearDate = () => {
    if (selectedDate) {
      setSelectedDate(null);

      dispatch(
        reducerTemplateUnloadingsInputValues({
          ...templateUnloadingsInputValues,
          date: "",
        })
      );

      setIsPending(true);
      dispatch(actionGetTemplateUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    }
  };

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleopenModal = () => {
    dispatch(reducerTemplateOpenErrorsModal(!openErrorsModal));
  };

  const handleClickError = (elem: ITemplateUnloadings) => {
    setIsOpenModal(elem.error);
    handleopenModal();
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionGetTemplateUnloadings(newLimit, newPage)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  const handleClickRefreshBtn = () => {
    setIsPending(true);
    dispatch(actionGetTemplateUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  useEffect(() => {
    const handleDocumentClick = () => {
      if (isInputChange) {
        setIsPending(true);
        dispatch(actionGetTemplateUnloadings(limit, page)).then(() => {
          setIsPending(false);
        });
        setIsInputChange(false);
        resetPages();
      }
    };
    if (isInputChange) {
      document.addEventListener("click", handleDocumentClick);
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
    } else
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
  }, [dispatch, isInputChange, limit, page]);

  return (
    <>
      <div className="loader__wrapper">
        {(!templateUnloadings || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__data_picker filter__data_picker_excel">
              <DatePicker
                onChange={handleDatePickerChange}
                onKeyDown={handleDateKeyDown}
                selected={selectedDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Дата"
                autoComplete="off"
                locale="ru"
                name="date"
              />
              {selectedDate && (
                <div className="clear__icon" onClick={handleClearDate} />
              )}
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  templateUnloadingsInputValues.employee &&
                  createOptions(filters, "type")?.find(
                    (option: { value: string; label: string }) =>
                      option.value === templateUnloadingsInputValues.employee.id
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "type")
                }
                isShowPlaceholder={
                  (templateUnloadingsInputValues?.employee &&
                    !!Object.keys(templateUnloadingsInputValues?.employee)
                      .length) ||
                  templateUnloadingsInputValues?.employee === undefined
                }
                options={typeOptions}
                name="type"
                className="users__filter"
                placeholder="Тип"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  templateUnloadingsInputValues.employee &&
                  createOptions(filters, "employees")?.find(
                    (option: { value: string; label: string }) =>
                      option.value === templateUnloadingsInputValues.employee.id
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "employee")
                }
                isShowPlaceholder={
                  (templateUnloadingsInputValues?.employee &&
                    !!Object.keys(templateUnloadingsInputValues?.employee)
                      .length) ||
                  templateUnloadingsInputValues?.employee === undefined
                }
                options={createOptions(filters, "employees")}
                name="employees"
                className="users__filter"
                placeholder="Сотрудник"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  templateUnloadingsInputValues.status &&
                  statusesOptions.find(
                    (option: { value: string; label: string }) =>
                      option.value === templateUnloadingsInputValues.status
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "status")
                }
                isShowPlaceholder={
                  !!templateUnloadingsInputValues.status ||
                  selectedStatusOption === "all"
                }
                options={statusesOptions}
                name="status"
                className="users__filter"
                placeholder="Статус"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  templateUnloadingsInputValues.moreThanMin &&
                  timeOptions.find(
                    (option: { value: string; label: string }) =>
                      parseFloat(option.value) ===
                      templateUnloadingsInputValues.moreThanMin
                  )
                }
                propsChange={(event: { value: string; label: string }) => {
                  selectChange(event, "moreThanMin");
                }}
                isShowPlaceholder={
                  !!templateUnloadingsInputValues.moreThanMin ||
                  templateUnloadingsInputValues.moreThanMin === undefined
                }
                placeholder="Время выполнения"
                name="moreThanMin"
                className="users__filter"
                options={timeOptions}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="refresh__btn" onClick={handleClickRefreshBtn}>
              <img className="refresh__icon" src={RefreshIcon} alt="refresh" />
            </div>
          </div>
        </div>
        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr className="unloadings__filter_tr">
                <th className="reports__number_th">№</th>
                <th>
                  <div className="filter__data_picker filter__data_picker_excel">
                    <DatePicker
                      onChange={handleDatePickerChange}
                      onKeyDown={handleDateKeyDown}
                      selected={selectedDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Дата"
                      autoComplete="off"
                      locale="ru"
                      name="date"
                    />
                    {selectedDate && (
                      <div className="clear__icon" onClick={handleClearDate} />
                    )}
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        templateUnloadingsInputValues.employee &&
                        createOptions(filters, "type")?.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            templateUnloadingsInputValues.employee.id
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "type")
                      }
                      isShowPlaceholder={
                        (templateUnloadingsInputValues?.employee &&
                          !!Object.keys(templateUnloadingsInputValues?.employee)
                            .length) ||
                        templateUnloadingsInputValues?.employee === undefined
                      }
                      options={typeOptions}
                      name="type"
                      className="users__filter"
                      placeholder="Тип"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        templateUnloadingsInputValues.employee &&
                        createOptions(filters, "employees")?.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            templateUnloadingsInputValues.employee.id
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "employee")
                      }
                      isShowPlaceholder={
                        (templateUnloadingsInputValues?.employee &&
                          !!Object.keys(templateUnloadingsInputValues?.employee)
                            .length) ||
                        templateUnloadingsInputValues?.employee === undefined
                      }
                      options={createOptions(filters, "employees")}
                      name="employees"
                      className="users__filter"
                      placeholder="Сотрудник"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        templateUnloadingsInputValues.status &&
                        statusesOptions.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            templateUnloadingsInputValues.status
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "status")
                      }
                      isShowPlaceholder={
                        !!templateUnloadingsInputValues.status ||
                        selectedStatusOption === "all"
                      }
                      options={statusesOptions}
                      name="status"
                      className="users__filter"
                      placeholder="Статус"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        templateUnloadingsInputValues.moreThanMin &&
                        timeOptions.find(
                          (option: { value: string; label: string }) =>
                            parseFloat(option.value) ===
                            templateUnloadingsInputValues.moreThanMin
                        )
                      }
                      propsChange={(event: {
                        value: string;
                        label: string;
                      }) => {
                        selectChange(event, "moreThanMin");
                      }}
                      isShowPlaceholder={
                        !!templateUnloadingsInputValues.moreThanMin ||
                        templateUnloadingsInputValues.moreThanMin === undefined
                      }
                      placeholder="Время выполнения"
                      name="moreThanMin"
                      className="users__filter"
                      options={timeOptions}
                    />
                  </div>
                </th>
                <th className="unloadings__link_cell">Файл загрузки</th>
                <th className="unloadings__link_cell">Лог </th>
                <th>
                  <div className="refresh__btn" onClick={handleClickRefreshBtn}>
                    <img
                      className="refresh__icon"
                      src={RefreshIcon}
                      alt="refresh"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {templateUnloadings &&
                templateUnloadings.map(
                  (elem: ITemplateUnloadings, index: number) => (
                    <tr key={index}>
                      <td>{elem?.order?.id}</td>
                      <td>{formatUnloadingsDate(elem?.date)}</td>
                      <td>{elem?.type}</td>
                      <td>{elem?.employee}</td>
                      <td>{Object.values(elem.status)}</td>
                      <td>{formatUnloadingsTime(elem.time)}</td>
                      <td>
                        {elem?.url && (
                          <a href={elem?.url} download className="report__link">
                            Скачать
                          </a>
                        )}
                      </td>
                      <td>
                        {elem.error && (
                          <span
                            className="report__link"
                            onClick={() => handleClickError(elem)}
                          >
                            Посмотреть ошибку
                          </span>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  )
                )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">
            Всего: {templateUnloadingsCount}
          </div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            count={templateUnloadingsCount}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
            pageName="userReports"
          />
        </div>
      </div>
      {openErrorsModal && (
        <ErrorsModal
          title={"Описание ошибки"}
          onClose={handleopenModal}
          content={isOpenModal}
        />
      )}
    </>
  );
};

export default TableAdminUnloadingTemplate;
